import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpHandlerFn
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { DialogService } from '../services/dialog.service';
import { Router } from '@angular/router';

// Functional Interceptor สำหรับ Angular 17+
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const dialogService = inject(DialogService);
  const router = inject(Router);
  
  // Skip auth header สำหรับ public endpoints
  const publicEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/health'];
  const isPublicEndpoint = publicEndpoints.some(endpoint => req.url.includes(endpoint));
  
  // ถ้าเป็น public endpoint หรือไม่ใช่ API request ให้ส่งต่อโดยไม่ต้องใส่ token
  if (isPublicEndpoint) {
    return next(req);
  }
  
  // ดึง token และแนบไปกับ request
  const token = authService.getAccessToken();
  let authReq = req;
  
  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
  
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // ตรวจสอบว่าเป็น token error หรือไม่
      const errorCode = error.error?.error?.code || error.error?.code;
      const isTokenExpired = error.status === 401 && errorCode === 'TOKEN_EXPIRED';
      const isTokenInvalid = error.status === 401 && (errorCode === 'INVALID_TOKEN' || errorCode === 'INVALID_TOKEN_FORMAT' || errorCode === 'NO_TOKEN');
      const isTokenError = error.status === 401;
      
      if (isTokenError) {
        const user = authService.getCurrentUser();
        
        // ถ้าเป็น OAuth/Demo user และ token หมดอายุ ให้ลอง refresh ก่อน
        // ถ้า token invalid จริงๆ ค่อย logout
        if (isTokenInvalid) {
          console.warn('🔐 Invalid token (not expired) - logging out immediately', errorCode);
          return from(dialogService.showTokenExpiredDialog()).pipe(
            switchMap(() => {
              authService.logoutSync();
              router.navigate(['/login'], { 
                queryParams: { reason: 'session_expired' } 
              });
              return throwError(() => error);
            })
          );
        }
        
        // ถ้าเป็น OAuth/Demo user และ token แค่ expired ให้ logout (ไม่มี refresh token)
        if (user && (user.provider === 'google' || user.provider === 'facebook' || user.provider === 'line' || user.provider === 'demo') && isTokenExpired) {
          console.warn('🔐 OAuth/Demo user token expired - logging out');
          return from(dialogService.showTokenExpiredDialog()).pipe(
            switchMap(() => {
              authService.logoutSync();
              router.navigate(['/login'], { 
                queryParams: { reason: 'session_expired' } 
              });
              return throwError(() => error);
            })
          );
        }
        
        // ถ้าเป็น local user หรือ token expired ให้ลอง refresh token
        console.warn('🔐 Token expired - attempting refresh', errorCode);
        return authService.refreshToken().pipe(
          switchMap(response => {
            // ถ้า refresh สำเร็จ ให้ลอง request ใหม่
            console.log('✅ Token refreshed successfully');
            const newToken = authService.getAccessToken();
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`
              }
            });
            return next(retryReq);
          }),
          catchError(refreshError => {
            // ถ้า refresh ไม่สำเร็จ แสดง popup แจ้งเตือนก่อน redirect
            console.error('❌ Token refresh failed - logging out');
            return from(dialogService.showTokenExpiredDialog()).pipe(
              switchMap(() => {
                authService.logoutSync();
                router.navigate(['/login'], { 
                  queryParams: { reason: 'session_expired' } 
                });
                return throwError(() => refreshError);
              })
            );
          })
        );
      }
      
      return throwError(() => error);
    })
  );
};

// Class-based Interceptor (สำหรับ backward compatibility)
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private dialogService: DialogService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth header สำหรับ public endpoints
    const publicEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/health'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => request.url.includes(endpoint));
    
    if (isPublicEndpoint) {
      return next.handle(request);
    }

    // ดึง token และแนบไปกับ request
    const token = this.authService.getAccessToken();
    
    if (token) {
      request = this.addTokenToRequest(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // ตรวจสอบว่าเป็น token error หรือไม่
        const isTokenError = error.status === 401;
        
        if (isTokenError) {
          return this.handleTokenError(request, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handleTokenError(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    const user = this.authService.getCurrentUser();
    const errorCode = error.error?.error?.code || error.error?.code;
    const isTokenInvalid = errorCode === 'INVALID_TOKEN' || errorCode === 'INVALID_TOKEN_FORMAT' || errorCode === 'NO_TOKEN';
    const isTokenExpired = errorCode === 'TOKEN_EXPIRED';

    // ถ้า token invalid จริงๆ (ไม่ใช่แค่ expired) ให้ logout ทันที
    if (isTokenInvalid) {
      console.warn('🔐 Invalid token - logging out immediately', errorCode);
      return from(this.dialogService.showTokenExpiredDialog()).pipe(
        switchMap(() => {
          this.authService.logoutSync();
          this.router.navigate(['/login'], { 
            queryParams: { reason: 'session_expired' } 
          });
          return throwError(() => error);
        })
      );
    }

    // ถ้าเป็น OAuth/Demo user และ token expired ให้ logout (ไม่มี refresh token)
    if (user && (user.provider === 'google' || user.provider === 'facebook' || user.provider === 'line' || user.provider === 'demo') && isTokenExpired) {
      console.warn('🔐 OAuth/Demo user token expired - logging out');
      return from(this.dialogService.showTokenExpiredDialog()).pipe(
        switchMap(() => {
          this.authService.logoutSync();
          this.router.navigate(['/login'], { 
            queryParams: { reason: 'session_expired' } 
          });
          return throwError(() => error);
        })
      );
    }

    // ถ้าเป็น local user และเป็นแค่ expired ให้ลอง refresh token
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      console.warn('🔐 Token expired - attempting refresh');
      return this.authService.refreshToken().pipe(
        switchMap(response => {
          this.isRefreshing = false;
          console.log('✅ Token refreshed successfully');
          const newToken = this.authService.getAccessToken();
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addTokenToRequest(request, newToken!));
        }),
        catchError(refreshError => {
          this.isRefreshing = false;
          // แสดง popup แจ้งเตือนก่อน redirect
          console.error('❌ Token refresh failed - logging out');
          return from(this.dialogService.showTokenExpiredDialog()).pipe(
            switchMap(() => {
              this.authService.logoutSync();
              this.router.navigate(['/login'], { 
                queryParams: { reason: 'session_expired' } 
              });
              return throwError(() => refreshError);
            })
          );
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // ถ้ากำลัง refresh อยู่ ให้รอ
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          return next.handle(this.addTokenToRequest(request, token!));
        })
      );
    }
  }
}
